import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Stack,
  LinearProgress,
} from '@mui/material';
import {
  CloudSync,
  CheckCircle,
  Error,
  Refresh,
  Description,
  Settings,
  Save,
  Science,
  Business,
  Upload,
  Folder as FolderIcon,
  DragIndicator,
} from '@mui/icons-material';
import { apiService } from '../services/api';
import EnhancedFilesTable from './EnhancedFilesTable';
import FolderTree from './FolderTree';

interface FranConnectConfigState {
  apiKey: string;
  baseUrl: string;
  username: string;
  isConfigured: boolean;
  lastSync?: string;
  totalAssets?: number;
  franConnectCount?: number;
}

interface SyncResult {
  status: string;
  syncedCount?: number;
  errorCount?: number;
  totalAssets?: number;
  message?: string;
}

const FranConnectConfig: React.FC = () => {
  // Real-time upload progress with LinearProgress - v2
  const [config, setConfig] = useState<FranConnectConfigState>({
    apiKey: '',
    baseUrl: '',
    username: '',
    isConfigured: false
  });
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<'unknown' | 'success' | 'error'>('unknown');
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
  const [uploadResult, setUploadResult] = useState<{ uploaded: number; failed: number; files: string[] } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadedDocs, setUploadedDocs] = useState<any[]>([]);
  const [totalDocs, setTotalDocs] = useState<number>(0);
  const [polling, setPolling] = useState<boolean>(false);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [selectedFolderName, setSelectedFolderName] = useState<string>('All Files');
  
  // Resizable sidebar state
  const [sidebarWidth, setSidebarWidth] = useState<number>(350);
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  
  // Handle sidebar resize
  const startResizing = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);
  
  const stopResizing = useCallback(() => {
    setIsResizing(false);
  }, []);
  
  const resize = useCallback((e: MouseEvent) => {
    if (isResizing && sidebarRef.current) {
      const sidebarRect = sidebarRef.current.getBoundingClientRect();
      const newWidth = e.clientX - sidebarRect.left;
      // Clamp between min and max width
      const clampedWidth = Math.min(Math.max(newWidth, 180), 500);
      setSidebarWidth(clampedWidth);
    }
  }, [isResizing]);
  
  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', resize);
      window.addEventListener('mouseup', stopResizing);
    }
    return () => {
      window.removeEventListener('mousemove', resize);
      window.removeEventListener('mouseup', stopResizing);
    };
  }, [isResizing, resize, stopResizing]);

  useEffect(() => {
    loadConfiguration();
    loadUploadedDocuments(null);
    checkForRunningSync();
  }, []);

  // Poll for status updates while there are active docs
  useEffect(() => {
    let intervalId: any;
    if (polling) {
      intervalId = setInterval(() => {
        loadUploadedDocuments(selectedFolderId);
      }, 2000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [polling, selectedFolderId]);

  const getErrorMessage = (error: any, fallback: string): string => {
    try {
      const detail = error?.response?.data?.detail;
      if (!detail) {
        return error?.message || fallback;
      }
      if (typeof detail === 'string') {
        return detail;
      }
      if (Array.isArray(detail)) {
        const messages = detail
          .map((d: any) => d?.msg || d?.message || JSON.stringify(d))
          .filter(Boolean);
        return messages.join('; ') || fallback;
      }
      if (typeof detail === 'object') {
        if (detail.msg || detail.message) {
          return detail.msg || detail.message;
        }
        return JSON.stringify(detail);
      }
      return String(detail);
    } catch {
      return fallback;
    }
  }

  const loadConfiguration = async () => {
    try {
      setLoading(true);
      // Use apiService to ensure the /burngpt base URL prefix is applied (v2)
      const response = await apiService.get('/api/v1/sources/franconnect/config');
      const data = response?.data;
      
      if (data) {
        const isConfigured = Boolean(data.api_key && data.base_url && data.enabled);
        
        setConfig((prev) => ({
          ...prev,
          apiKey: data.api_key ? '••••••••' : '',
          baseUrl: data.base_url || '',
          username: data.username || '',
          isConfigured,
          lastSync: data.last_modified
        }));
      }

      // Load document stats to populate counts
      try {
        const stats = await apiService.getDocumentStats();
        const franConnectCount = stats?.by_source?.franconnect || 0;
        setConfig((prev) => ({ ...prev, franConnectCount, totalAssets: franConnectCount }));
      } catch {}
    } catch (error) {
      console.error('Failed to load FranConnect configuration:', error);
      setMessage({ type: 'error', text: 'Failed to load configuration' });
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async () => {
    if (!config.apiKey || !config.baseUrl) {
      setMessage({ type: 'error', text: 'Please fill in both API Key and Base URL' });
      return;
    }

    try {
      setTesting(true);
      setMessage(null);
      
      const payload = {
        api_key: config.apiKey === '••••••••' ? undefined : config.apiKey,
        base_url: config.baseUrl,
        username: config.username
      };
      const response = await apiService.post('/api/v1/sources/franconnect/test', payload);
      const data = response?.data || {};

      if (data.status === 'success') {
        setConnectionStatus('success');
        setMessage({ 
          type: 'success', 
          text: `✅ Connected successfully! FranConnect API is accessible` 
        });
      } else {
        setConnectionStatus('error');
        setMessage({ 
          type: 'error', 
          text: data.message || 'Connection failed' 
        });
      }
    } catch (error: any) {
      setConnectionStatus('error');
      setMessage({ 
        type: 'error', 
        text: getErrorMessage(error, 'Connection test failed')
      });
    } finally {
      setTesting(false);
    }
  };

  const saveConfiguration = async () => {
    if (!config.apiKey || !config.baseUrl) {
      setMessage({ type: 'error', text: 'Please fill in both API Key and Base URL' });
      return;
    }

    try {
      setLoading(true);
      setMessage(null);

      const payload = {
        api_key: config.apiKey === '••••••••' ? undefined : config.apiKey,
        base_url: config.baseUrl,
        username: config.username
      };
      const response = await apiService.post('/api/v1/sources/franconnect/config', payload);

      if (response.status >= 200 && response.status < 300) {
        setMessage({ type: 'success', text: 'Configuration saved successfully!' });
        setConfig(prev => ({ ...prev, isConfigured: true }));
        
        // Reload configuration to get updated status
        setTimeout(() => {
          loadConfiguration();
          loadUploadedDocuments(selectedFolderId);
        }, 1000);
      } else {
        const error = response?.data || {};
        setMessage({ 
          type: 'error', 
          text: error.detail || 'Failed to save configuration'
        });
      }
      
    } catch (error: any) {
      setMessage({ 
        type: 'error', 
        text: getErrorMessage(error, 'Failed to save configuration')
      });
    } finally {
      setLoading(false);
    }
  };

  const syncAssets = async () => {
    try {
      setSyncing(true);
      setMessage(null);
      setSyncResult(null);

      const response = await apiService.post('/api/v1/sources/franconnect/sync', {});
      const jobId = response.data?.job_id;

      if (jobId) {
        setMessage({ type: 'info', text: 'S3 sync started. Monitoring progress...' });
        pollS3SyncProgress(jobId);
      } else {
        setMessage({ type: 'success', text: response.data?.message || 'Sync triggered' });
        setSyncing(false);
      }
    } catch (error: any) {
      setMessage({ 
        type: 'error', 
        text: getErrorMessage(error, 'S3 sync failed')
      });
      setSyncing(false);
    }
  };

  const pollS3SyncProgress = (jobId: string) => {
    const poll = async () => {
      try {
        const res = await apiService.get(`/api/v1/jobs/${jobId}`);
        const job = res.data;

        if (job.status === 'completed' || job.status === 'failed') {
          setSyncing(false);
          if (job.status === 'completed') {
            const r = job.result || {};
            setSyncResult({
              status: 'success',
              syncedCount: (r.added || 0) + (r.updated || 0),
              errorCount: r.failed || 0,
              totalAssets: r.total_s3_files || 0,
              message: `Added ${r.added || 0}, updated ${r.updated || 0}, skipped ${r.skipped || 0}`,
            });
            setMessage({ type: 'success', text: `S3 sync complete! ${r.added || 0} added, ${r.updated || 0} updated, ${r.skipped || 0} unchanged` });
          } else {
            setMessage({ type: 'error', text: `Sync failed: ${job.error_message || 'Unknown error'}` });
          }
          loadConfiguration();
          loadUploadedDocuments(selectedFolderId);
          return;
        }

        if (job.progress) {
          const pct = job.progress.total > 0
            ? Math.round((job.progress.current / job.progress.total) * 100)
            : 0;
          setMessage({ type: 'info', text: `Syncing... ${pct}% (${job.progress.current}/${job.progress.total}) — ${job.progress.message || ''}` });
        }

        setTimeout(poll, 3000);
      } catch {
        setTimeout(poll, 5000);
      }
    };
    setTimeout(poll, 2000);
  };

  const checkForRunningSync = async () => {
    try {
      const res = await apiService.get('/api/v1/jobs?job_type=franconnect_sync&status=running&limit=1');
      const jobs = res.data;
      if (jobs && jobs.length > 0) {
        const job = jobs[0];
        setSyncing(true);
        const pct = job.progress?.total > 0
          ? Math.round((job.progress.current / job.progress.total) * 100)
          : 0;
        setMessage({ type: 'info', text: `S3 sync in progress... ${pct}% (${job.progress?.current || 0}/${job.progress?.total || 0})` });
        pollS3SyncProgress(job.job_id);
      }
    } catch {
      // Silently ignore - no running sync
    }
  };

  const handleManualUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [jobProgress, setJobProgress] = useState<any>(null);
  const [progressInterval, setProgressInterval] = useState<any>(null);
  const [uploadTimeout, setUploadTimeout] = useState<any>(null);
  
  // Poll for job progress
  useEffect(() => {
    return () => {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      if (uploadTimeout) {
        clearTimeout(uploadTimeout);
      }
    };
  }, [progressInterval, uploadTimeout]);

  // Fallback timeout to reset upload state if something goes wrong
  const resetUploadState = () => {
    setUploading(false);
    setJobProgress(null);
    setCurrentJobId(null);
    if (progressInterval) {
      clearInterval(progressInterval);
      setProgressInterval(null);
    }
    if (uploadTimeout) {
      clearTimeout(uploadTimeout);
      setUploadTimeout(null);
    }
  };

  const pollJobProgress = async (jobId: string) => {
    // Set a fallback timeout to reset state after 2 minutes max
    const timeout = setTimeout(() => {
      console.log('Upload timeout - resetting state');
      setMessage({ type: 'info', text: 'Upload processing in background. Refreshing...' });
      resetUploadState();
      loadUploadedDocuments(selectedFolderId);
    }, 120000);
    setUploadTimeout(timeout);

    const poll = async () => {
      try {
        const response = await apiService.get(`/api/v1/jobs/${jobId}`);
        const job = response.data;
        
        if (job.status === 'completed' || job.status === 'failed') {
          // Clear the fallback timeout
          if (uploadTimeout) {
            clearTimeout(uploadTimeout);
            setUploadTimeout(null);
          }
          
          if (progressInterval) {
            clearInterval(progressInterval);
            setProgressInterval(null);
          }
          
          if (job.status === 'completed') {
            setMessage({ 
              type: 'success', 
              text: `✅ Upload completed! ${job.result?.succeeded || 0} files processed successfully` 
            });
            setUploadResult({
              uploaded: job.result?.succeeded || 0,
              failed: job.result?.failed || 0,
              files: []
            });
          } else {
            setMessage({ 
              type: 'error', 
              text: `Upload failed: ${job.error || 'Unknown error'}` 
            });
          }
          
          setUploading(false);
          setJobProgress(null);
          setCurrentJobId(null);
          
          // Reload documents
          setTimeout(() => {
            loadConfiguration();
            loadUploadedDocuments(selectedFolderId);
          }, 1000);
          
          return;
        }
        
        // Update progress
        if (job.progress) {
          const percentage = job.progress.total > 0 
            ? (job.progress.current / job.progress.total) * 100 
            : 0;
          
          setJobProgress({
            progress: {
              current: job.progress.current,
              total: job.progress.total,
              message: job.progress.message,
              percentage,
              details: job.progress.details
            }
          });
        }
      } catch (error) {
        console.error('Error polling job progress:', error);
        // After 3 failed polls, reset state
        resetUploadState();
        setMessage({ type: 'info', text: 'Upload processing. Refresh the page to see results.' });
        loadUploadedDocuments(selectedFolderId);
      }
    };
    
    // Initial poll
    await poll();
    
    // Set up interval for subsequent polls
    const interval = setInterval(poll, 1000);
    setProgressInterval(interval);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    try {
      setUploading(true);
      setMessage(null);
      setUploadResult(null);
      setJobProgress(null);

      const formData = new FormData();
      const fileNames: string[] = [];
      
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
        fileNames.push(files[i].name);
      }
      
      // Add source_type to specify this is from FranConnect config page
      formData.append('source_type', 'franconnect');
      
      // Add folder_id if a folder is selected
      if (selectedFolderId && selectedFolderId !== 'root') {
        formData.append('folder_id', selectedFolderId);
      }

      const response = await apiService.postForm('/api/v1/documents/upload', formData);

      if (response.data.success) {
        const jobId = response.data.job_id;
        
        if (jobId) {
          // Start polling for job progress
          setCurrentJobId(jobId);
          pollJobProgress(jobId);
        } else {
          // Fallback to old behavior if no job_id - immediate completion
          setUploadResult({
            uploaded: response.data.uploaded || fileNames.length,
            failed: response.data.failed || 0,
            files: fileNames
          });
          setMessage({ 
            type: 'success', 
            text: `✅ Successfully uploaded ${response.data.uploaded || fileNames.length} files!` 
          });
          
          // Reset upload state immediately
          setUploading(false);
          
          setTimeout(() => {
            loadConfiguration();
            loadUploadedDocuments(selectedFolderId);
            apiService.getDocumentStats().then((stats) => {
              const franConnectCount = stats?.by_source?.franconnect || 0;
              setConfig((prev) => ({ ...prev, franConnectCount, totalAssets: franConnectCount }));
            }).catch(() => {});
          }, 1000);
        }
      } else {
        setMessage({ 
          type: 'error', 
          text: response.data.message || 'Upload failed' 
        });
        setUploading(false);
      }
    } catch (error: any) {
      setMessage({ 
        type: 'error', 
        text: getErrorMessage(error, 'Upload failed')
      });
      setUploading(false);
    } finally {
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const loadUploadedDocuments = async (folderId?: string | null) => {
    const targetFolderId = folderId !== undefined ? folderId : selectedFolderId;
    console.log('Loading documents for folder:', targetFolderId);
    
    try {
      if (targetFolderId && targetFolderId !== 'root' && targetFolderId !== null) {
        // Load files from specific folder
        console.log('Fetching folder files from:', `/api/v1/folders/${targetFolderId}/files`);
        const res = await apiService.get(`/api/v1/folders/${targetFolderId}/files?skip=0&limit=50`);
        console.log('Folder files response:', res.data);
        const files = (res.data.files || []).map((f: any) => ({
          id: f.id,
          title: f.title,
          status: f.status || 'completed',
          created_at: f.created_at,
          updated_at: f.created_at,
          metadata: {
            file_size: f.file_size,
            custom_fields: {
              public_url: f.document_url,
            }
          },
          source_type: 'franconnect',
          in_knowledge_base: f.in_knowledge_base !== false
        }));
        setUploadedDocs(files);
        setTotalDocs(res.data.total || 0);
        return; // Exit early on success
      } else if (targetFolderId === 'root') {
        // Load root files (no folder)
        console.log('Fetching root files');
        const res = await apiService.get(`/api/v1/folders/root/files?skip=0&limit=50`);
        console.log('Root files response:', res.data);
        const files = (res.data.files || []).map((f: any) => ({
          id: f.id,
          title: f.title,
          status: f.status || 'completed',
          created_at: f.created_at,
          updated_at: f.created_at,
          metadata: {
            file_size: f.file_size,
            custom_fields: {
              public_url: f.document_url,
            }
          },
          source_type: 'franconnect',
          in_knowledge_base: f.in_knowledge_base !== false
        }));
        setUploadedDocs(files);
        setTotalDocs(res.data.total || 0);
        return; // Exit early on success
      }
    } catch (e) {
      console.error('Error loading folder documents:', e);
      // Don't fallback - show error state
      setUploadedDocs([]);
      setTotalDocs(0);
      return;
    }
    
    // Default: Load all files (when folderId is null)
    try {
      console.log('Loading all documents');
      const res = await apiService.getDocuments({ page: 1, per_page: 50, source_type: 'franconnect' });
      setUploadedDocs(res.documents || []);
      setTotalDocs(res.total || 0);
      const hasActive = (res.documents || []).some((d: any) => ['pending','processing'].includes((d.status || '').toString().toLowerCase()));
      setPolling(hasActive);
    } catch (e) {
      console.error('Error loading all documents:', e);
    }
  };
  
  const handleFolderSelect = (folderId: string | null, folderName: string, folderPath: string) => {
    console.log('handleFolderSelect called with:', { folderId, folderName, folderPath });
    setSelectedFolderId(folderId);
    setSelectedFolderName(folderName);
    loadUploadedDocuments(folderId);
  };
  

  const handleLoadMoreDocuments = async (page: number, perPage: number, search?: string) => {
    const res = await apiService.getDocuments({ page, per_page: perPage, source_type: 'franconnect', search });
    return {
      documents: res.documents || [],
      total: res.total || 0
    };
  };

  const handleKbToggle = async (fileId: string, inKb: boolean) => {
    try {
      await apiService.put(`/api/v1/folders/files/${fileId}/kb?in_kb=${inKb}`);
      
      // Update local state
      setUploadedDocs(prev => prev.map(doc => 
        doc.id === fileId ? { ...doc, in_knowledge_base: inKb } : doc
      ));
      
      setMessage({ 
        type: 'success', 
        text: `File ${inKb ? 'added to' : 'removed from'} knowledge base` 
      });
    } catch (error: any) {
      setMessage({ 
        type: 'error', 
        text: getErrorMessage(error, 'Failed to update knowledge base setting')
      });
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', px: { xs: 1, md: 2 }, py: 2, overflowX: 'hidden', boxSizing: 'border-box' }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Business color="primary" />
          FranConnect Configuration
        </Typography>
        
        <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 3 }}>
          Configure your FranConnect integration to access franchise documents, policies, and procedures.
        </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

        <Box sx={{ display: 'flex', gap: 2, mb: 3, width: '100%', flexWrap: 'wrap' }}>
          {/* Left Column - API Configuration */}
          <Card sx={{ flex: '1 1 350px', minWidth: 0 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Settings />
                API Configuration
              </Typography>

              <Stack spacing={3}>
                <TextField
                  fullWidth
                  label="FranConnect Base URL"
                  value={config.baseUrl}
                  onChange={(e) => setConfig(prev => ({ ...prev, baseUrl: e.target.value }))}
                  placeholder="https://your-company.franconnect.com"
                  helperText="Your FranConnect instance URL"
                  disabled={loading}
                />
                
                <TextField
                  fullWidth
                  label="API Key"
                  type="password"
                  value={config.apiKey}
                  onChange={(e) => setConfig(prev => ({ ...prev, apiKey: e.target.value }))}
                  placeholder="Enter your FranConnect API key"
                  helperText="Your FranConnect API authentication key"
                  disabled={loading}
                />

                <TextField
                  fullWidth
                  label="Username (Optional)"
                  value={config.username}
                  onChange={(e) => setConfig(prev => ({ ...prev, username: e.target.value }))}
                  placeholder="API username if required"
                  helperText="Some FranConnect APIs require a username"
                  disabled={loading}
                />
              </Stack>

              <Box sx={{ mt: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  startIcon={testing ? <CircularProgress size={20} /> : <Science />}
                  onClick={testConnection}
                  disabled={testing || loading}
                >
                  Test Connection
                </Button>
                
                <Button
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                  onClick={saveConfiguration}
                  disabled={loading || testing}
                >
                  Save Configuration
                </Button>

                {connectionStatus === 'success' && (
                  <Chip icon={<CheckCircle />} label="Connected" color="success" size="small" />
                )}
                {connectionStatus === 'error' && (
                  <Chip icon={<Error />} label="Connection Failed" color="error" size="small" />
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Right Column - Status Overview & Document Management Combined */}
          <Card sx={{ flex: '1 1 350px', minWidth: 0 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Current Status
              </Typography>
              
              <Stack spacing={2} sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body1">Configuration</Typography>
                  <Chip 
                    label={config.isConfigured ? 'Configured' : 'Not Configured'} 
                    color={config.isConfigured ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body1">FranConnect Documents</Typography>
                  <Typography variant="h6" color="primary">{config.franConnectCount || 0}</Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body1">Last Sync</Typography>
                  <Typography variant="body2" color="text.secondary">{config.lastSync ? new Date(config.lastSync).toLocaleDateString() : 'Never'}</Typography>
                </Box>
              </Stack>

              {/* Document Management Section */}
              <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CloudSync fontSize="small" />
                  Document Management
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: '1 1 140px', minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      S3 Sync
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={syncing ? <CircularProgress size={14} /> : <CloudSync />}
                      onClick={syncAssets}
                      disabled={syncing}
                      color="secondary"
                      fullWidth
                    >
                      {syncing ? 'Syncing...' : 'Sync from S3'}
                    </Button>
                  </Box>
                  
                  <Box sx={{ flex: '1 1 140px', minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      Manual Upload
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={uploading ? <CircularProgress size={14} /> : <Upload />}
                      onClick={handleManualUpload}
                      disabled={loading || uploading}
                      color="primary"
                      fullWidth
                    >
                      {uploading ? 'Uploading...' : 'Upload'}
                    </Button>
                  </Box>
                </Box>

                {syncing && (
                  <Box sx={{ mt: 2 }}>
                    <LinearProgress variant="indeterminate" color="secondary" />
                  </Box>
                )}

                {syncResult && (
                  <Paper sx={{ p: 1.5, bgcolor: 'grey.50', mt: 2 }}>
                    <Typography variant="caption" fontWeight={600}>Sync Results:</Typography>
                    <List dense disablePadding sx={{ mt: 0.5 }}>
                      <ListItem sx={{ py: 0, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 28 }}><CheckCircle color="success" fontSize="small" /></ListItemIcon>
                        <ListItemText 
                          primary={`${syncResult.syncedCount || 0} synced`}
                          primaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                      {syncResult.errorCount && syncResult.errorCount > 0 && (
                        <ListItem sx={{ py: 0, px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}><Error color="error" fontSize="small" /></ListItemIcon>
                          <ListItemText 
                            primary={`${syncResult.errorCount} errors`}
                            primaryTypographyProps={{ variant: 'caption' }}
                          />
                        </ListItem>
                      )}
                    </List>
                  </Paper>
                )}

                {uploadResult && (
                  <Paper sx={{ p: 1.5, bgcolor: 'grey.50', mt: 2 }}>
                    <Typography variant="caption" fontWeight={600}>Upload Results:</Typography>
                    <List dense disablePadding sx={{ mt: 0.5 }}>
                      <ListItem sx={{ py: 0, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 28 }}><CheckCircle color="success" fontSize="small" /></ListItemIcon>
                        <ListItemText 
                          primary={`${uploadResult.uploaded} uploaded`}
                          primaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                      {uploadResult.failed > 0 && (
                        <ListItem sx={{ py: 0, px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}><Error color="error" fontSize="small" /></ListItemIcon>
                          <ListItemText 
                            primary={`${uploadResult.failed} failed`}
                            primaryTypographyProps={{ variant: 'caption' }}
                          />
                        </ListItem>
                      )}
                    </List>
                  </Paper>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* File Browser with Folder Tree - v2 */}
        <Card sx={{ mt: 2, border: '1px solid #e0e0e0', overflow: 'hidden' }}>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ display: 'flex', minHeight: 450 }}>
              {/* Folder Tree Sidebar - Resizable */}
              <Box
                ref={sidebarRef}
                sx={{
                  width: sidebarWidth,
                  minWidth: 180,
                  maxWidth: 500,
                  borderRight: 1,
                  borderColor: 'divider',
                  bgcolor: 'grey.50',
                  flexShrink: 0,
                  overflow: 'auto',
                  position: 'relative',
                  transition: isResizing ? 'none' : 'width 0.1s ease',
                }}
              >
                <FolderTree
                  sourceType="franconnect"
                  selectedFolderId={selectedFolderId}
                  onFolderSelect={handleFolderSelect}
                  onRefresh={() => loadUploadedDocuments(selectedFolderId)}
                />
              </Box>
              
              {/* Resize Handle */}
              <Box
                onMouseDown={startResizing}
                sx={{
                  width: 6,
                  cursor: 'col-resize',
                  bgcolor: isResizing ? 'primary.light' : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  transition: 'background-color 0.2s',
                  '&:hover': {
                    bgcolor: 'primary.light',
                  },
                  '&:hover .drag-indicator': {
                    opacity: 1,
                  },
                }}
              >
                <DragIndicator 
                  className="drag-indicator"
                  sx={{ 
                    fontSize: 16, 
                    color: 'primary.main',
                    opacity: isResizing ? 1 : 0,
                    transition: 'opacity 0.2s',
                    transform: 'rotate(90deg)',
                  }} 
                />
              </Box>

              {/* Main Content Area */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
                {/* Files Table */}
                <Box sx={{ flex: 1, p: 2, overflow: 'auto' }}>
                  {uploadedDocs.length > 0 ? (
                    <EnhancedFilesTable 
                      files={uploadedDocs} 
                      title={selectedFolderName ? `${selectedFolderName} (${totalDocs})` : `FranConnect Files (${totalDocs})`}
                      onLoadMore={handleLoadMoreDocuments}
                      totalCount={totalDocs}
                      serverSidePagination={true}
                      hideColumns={['source_type']}
                      showKbToggle={true}
                      onKbToggle={handleKbToggle}
                    />
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                      <FolderIcon sx={{ fontSize: 64, color: 'grey.300', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary">
                        No files uploaded yet
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Upload files using the button above
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>
        
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileUpload}
          multiple
          accept=".pdf,.doc,.docx,.txt,.pptx,.xlsx,.xls,.csv,.jpg,.jpeg,.png,.gif,.mp4,.mov,.avi,.wmv,.flv,.webm,.mkv"
          style={{ display: 'none' }}
        />
    </Box>
  );
};

export default FranConnectConfig;